/**
 * MergeInvestorDialog
 * Lets a business user fold one duplicate investor (the "loser") into another
 * (the "survivor"). All contracts and related records move to the survivor and
 * the loser is deactivated. Used from the Stakeholder Management Investors tab
 * to clean up duplicate investor rows.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { MergeType as MergeTypeIcon } from '@mui/icons-material';
import { apiPost, extractApiError } from '../../utils/apiClient';
import logger from '../../utils/logger';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {Array} props.investors - full investor list (each: {id, name, type, client_code, total_contracts, active})
 * @param {Function} props.onClose - called to dismiss the dialog
 * @param {Function} props.onMerged - called after a successful merge (to refresh the list)
 * @param {object|null} [props.initialSurvivor] - optional investor to pre-select as survivor
 */
export default function MergeInvestorDialog({ open, investors = [], onClose, onMerged, initialSurvivor = null }) {
  const [survivor, setSurvivor] = useState(initialSurvivor);
  const [loser, setLoser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed survivor whenever the dialog (re)opens with a different seed.
  useEffect(() => {
    if (open) {
      setSurvivor(initialSurvivor);
      setLoser(null);
      setError(null);
      setSaving(false);
    }
  }, [open, initialSurvivor]);

  // Only active investors are sensible merge targets; the loser list also
  // excludes whatever is selected as survivor (can't merge into itself).
  const activeInvestors = useMemo(
    () => investors.filter((i) => i.active !== false),
    [investors]
  );
  const loserOptions = useMemo(
    () => activeInvestors.filter((i) => !survivor || i.id !== survivor.id),
    [activeInvestors, survivor]
  );

  const optionLabel = (i) => (i ? `${i.name}${i.client_code ? ` (${i.client_code})` : ''}` : '');

  const contractCount = (i) => (i && i.total_contracts != null ? Number(i.total_contracts) : 0);

  const canMerge = survivor && loser && survivor.id !== loser.id && !saving;

  const handleMerge = async () => {
    if (!canMerge) return;
    setSaving(true);
    setError(null);
    try {
      await apiPost('/stakeholders/investors/merge', {
        survivor_id: survivor.id,
        loser_id: loser.id,
      });
      onMerged?.({ survivor, loser });
      onClose?.();
    } catch (err) {
      logger.error('Investor merge failed', { message: err.message, status: err.response?.status });
      setError(extractApiError(err, 'Failed to merge investors. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const InvestorSummary = ({ investor }) => {
    if (!investor) return null;
    const n = contractCount(investor);
    return (
      <Box sx={{ mt: 0.5 }}>
        <Chip
          size="small"
          label={`${n} contract${n === 1 ? '' : 's'}`}
          color={n > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
        {investor.type && (
          <Chip size="small" label={investor.type} variant="outlined" sx={{ ml: 1 }} />
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeTypeIcon color="action" />
        Merge investors
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Combine a duplicate investor into another. All contracts and related
          records from the investor being removed are moved to the one you keep,
          and the removed investor is deactivated. This cannot be undone
          automatically.
        </Typography>

        <Autocomplete
          options={activeInvestors}
          value={survivor}
          onChange={(_e, v) => {
            setSurvivor(v);
            if (v && loser && v.id === loser.id) setLoser(null);
          }}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disabled={saving}
          renderInput={(params) => (
            <TextField {...params} label="Keep (survivor)" placeholder="Search investor…" />
          )}
        />
        <InvestorSummary investor={survivor} />

        <Box sx={{ my: 2 }} />

        <Autocomplete
          options={loserOptions}
          value={loser}
          onChange={(_e, v) => setLoser(v)}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disabled={saving || !survivor}
          renderInput={(params) => (
            <TextField {...params} label="Merge & remove (loser)" placeholder="Search investor…" />
          )}
        />
        <InvestorSummary investor={loser} />

        {survivor && loser && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            All records from <strong>{loser.name}</strong>
            {contractCount(loser) > 0 ? ` (${contractCount(loser)} contract${contractCount(loser) === 1 ? '' : 's'})` : ''}
            {' '}will be moved to <strong>{survivor.name}</strong>, and{' '}
            <strong>{loser.name}</strong> will be deactivated.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleMerge}
          variant="contained"
          color="warning"
          disabled={!canMerge}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <MergeTypeIcon />}
        >
          {saving ? 'Merging…' : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
